import React, { FC, useMemo } from "react"

import { ArrowDownOutlined, ArrowUpOutlined, HistoryOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"

import "./PriceHistory.scss"

// Интерфейс для данных из API (date как timestamp, diff - изменение)
interface IPriceHistoryRaw {
    date: number
    price: number
    diff: number
}

interface IPriceHistoryProps {
    price: string | number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    priceHistory: string | IPriceHistoryRaw[] | null | undefined
}

const formatPrice = (price: number): string => {
    return new Intl.NumberFormat("ru-RU").format(price)
}

const formatDate = (timestamp: number): string => {
    // timestamp приходит в секундах, конвертируем в миллисекунды
    const date = new Date(timestamp * 1000)
    return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    })
}

// Парсим price_history - может приходить как JSON строка или как массив
const parsePriceHistory = (priceHistory: string | IPriceHistoryRaw[] | null | undefined): IPriceHistoryRaw[] | null => {
    if (!priceHistory) return null
    
    // Если уже массив - возвращаем как есть
    if (Array.isArray(priceHistory)) {
        return priceHistory
    }
    
    // Если строка - парсим JSON
    if (typeof priceHistory === "string") {
        try {
            const parsed = JSON.parse(priceHistory)
            if (Array.isArray(parsed)) {
                return parsed
            }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            // Ошибка парсинга - возвращаем null
        }
    }
    
    return null
}

const PriceHistoryContent: FC<{ history: IPriceHistoryRaw[] }> = ({ history }) => {
    // Сортируем по дате (новые сверху)
    const sortedHistory = [ ...history ].sort((a, b) => b.date - a.date)

    return (
        <div className="price-history-tooltip">
            <div className="price-history-header">
                <HistoryOutlined />
                <span>История изменения цены</span>
            </div>
            <div className="price-history-list">
                {sortedHistory.map((item, index) => {
                    // Используем diff из API напрямую
                    const priceDiff = item.diff || 0
                    const isIncrease = priceDiff > 0

                    return (
                        <div key={index} className="price-history-item">
                            <div className="price-history-date">
                                {formatDate(item.date)}
                            </div>
                            <div className="price-history-value">
                                <span className="price-amount">
                                    {formatPrice(item.price)} ₽
                                </span>
                                {priceDiff !== 0 && (
                                    <span className={`price-diff ${isIncrease ? "increase" : "decrease"}`}>
                                        {isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                        {formatPrice(Math.abs(priceDiff))} ₽
                                    </span>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

const PriceHistory: FC<IPriceHistoryProps> = ({ price, priceHistory }) => {
    // Парсим историю цен
    const parsedHistory = useMemo(() => parsePriceHistory(priceHistory), [ priceHistory ])

    // Получаем последнее изменение цены (если diff !== 0)
    const getLastPriceChange = () => {
        if (!parsedHistory || parsedHistory.length === 0) return null
        
        // Сортируем по дате (новые в конце)
        const sortedHistory = [ ...parsedHistory ].sort((a, b) => a.date - b.date)
        
        // Ищем последнюю запись с diff !== 0
        for (let i = sortedHistory.length - 1; i >= 0; i--) {
            if (sortedHistory[i].diff !== 0) {
                return {
                    diff: sortedHistory[i].diff,
                    isIncrease: sortedHistory[i].diff > 0
                }
            }
        }
        
        return null
    }

    const priceChange = getLastPriceChange()

    // Если есть история (больше 1 записи) - показываем с тултипом
    if (parsedHistory && parsedHistory.length > 1) {
        return (
            <Tooltip 
                title={<PriceHistoryContent history={parsedHistory} />}
                placement="left"
                overlayClassName="price-history-overlay"
                color="#fff"
            >
                <div className="price-cell-wrapper">
                    <span className="price-cell price-with-history">
                        {price}
                    </span>
                    {priceChange && (
                        <div className={`price-change ${priceChange.isIncrease ? "increase" : "decrease"}`}>
                            {priceChange.isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                            <span>{priceChange.isIncrease ? "выросла" : "упала"}</span>
                        </div>
                    )}
                </div>
            </Tooltip>
        )
    }

    return <span className="price-cell">{price}</span>
}

export default PriceHistory
